import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { ChevronDown, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatFullName } from 'shared/utils';
import { UPLOAD_STATUSES } from './constants';

export function DataTable({
  columns,
  data,
  onRefresh,
  loading = false,
  workspaces = [],
  renderExpandedRow,
  expandedId = null,
  onExpandedChange = null,
}) {
  const [sorting, setSorting] = React.useState([{ id: 'createdAt', desc: true }]);
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState({});

  // Compute expanded state from expandedId prop
  const expanded = React.useMemo(() => {
    if (!expandedId) return {};
    const rowIndex = data.findIndex(row => row._id === expandedId);
    return rowIndex >= 0 ? { [rowIndex]: true } : {};
  }, [expandedId, data]);

  // Handle expansion changes
  const handleExpandedChange = React.useCallback(
    updater => {
      if (!onExpandedChange) return;

      const newExpanded = typeof updater === 'function' ? updater(expanded) : updater;
      const expandedKeys = Object.keys(newExpanded).filter(key => newExpanded[key]);

      if (expandedKeys.length > 0) {
        // Find the newly clicked row by comparing with current expanded state
        const currentExpandedKeys = Object.keys(expanded).filter(key => expanded[key]);
        const newlyExpandedKey = expandedKeys.find(key => !currentExpandedKeys.includes(key));

        if (newlyExpandedKey !== undefined) {
          // A new row was clicked - expand it (and collapse the old one)
          const rowIndex = parseInt(newlyExpandedKey);
          const row = data[rowIndex];
          if (row) {
            onExpandedChange(row._id);
          }
        } else {
          // Same row clicked again - collapse it
          onExpandedChange(null);
        }
      } else {
        onExpandedChange(null);
      }
    },
    [expanded, expandedId, data, onExpandedChange],
  );

  const uniqueCreators = React.useMemo(() => {
    const creators = new Set();
    data.forEach(upload => {
      if (upload.createdBy) {
        const user = upload.createdBy;
        const displayName = formatFullName(user);
        creators.add(displayName);
      } else {
        creators.add('System');
      }
    });
    return Array.from(creators).sort();
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onExpandedChange: handleExpandedChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      expanded,
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center space-x-2">
          <Select
            value={table.getColumn('workspaceId')?.getFilterValue() ?? 'all'}
            onValueChange={value =>
              table.getColumn('workspaceId')?.setFilterValue(value === 'all' ? '' : value)
            }>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All workspaces" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All workspaces</SelectItem>
              {workspaces.map(workspace => (
                <SelectItem key={workspace.id} value={workspace.id}>
                  {workspace.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={table.getColumn('status')?.getFilterValue() ?? 'all'}
            onValueChange={value =>
              table.getColumn('status')?.setFilterValue(value === 'all' ? '' : value)
            }>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value={UPLOAD_STATUSES.CREATED}>Created</SelectItem>
              <SelectItem value={UPLOAD_STATUSES.MAPPED}>Mapped</SelectItem>
              <SelectItem value={UPLOAD_STATUSES.PROCESSING}>Processing</SelectItem>
              <SelectItem value={UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE}>Extracted</SelectItem>
              <SelectItem value={UPLOAD_STATUSES.EXTRACTING_METADATA}>
                Extracting Metadata
              </SelectItem>
              <SelectItem value={UPLOAD_STATUSES.COMPLETED}>Completed</SelectItem>
              <SelectItem value={UPLOAD_STATUSES.FAILED}>Failed</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={table.getColumn('createdBy')?.getFilterValue() ?? 'all'}
            onValueChange={value =>
              table.getColumn('createdBy')?.setFilterValue(value === 'all' ? '' : value)
            }>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All creators" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All creators</SelectItem>
              {uniqueCreators.map(creator => (
                <SelectItem key={creator} value={creator}>
                  {creator}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter(column => column.getCanHide())
                .map(column => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={value => column.toggleVisibility(!!value)}>
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
          {onRefresh && (
            <Button variant="default" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          )}
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <React.Fragment key={row.id}>
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => row.toggleExpanded()}>
                    {row.getVisibleCells().map(cell => (
                      <TableCell
                        key={cell.id}
                        onClick={e => {
                          // Don't propagate click if it's the expander cell
                          if (cell.column.id === 'expander') {
                            e.stopPropagation();
                          }
                        }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && renderExpandedRow && (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="p-0 bg-muted/30">
                        <div className="p-4">{renderExpandedRow(row.original)}</div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Loading uploads...
                    </div>
                  ) : (
                    'No uploads found.'
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} row(s) total.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}>
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
